// The end of the battle (6 Oct): the details form with its offer, and the trial link each player gets in the mailshot.
// 9 Oct: the offer follows the trial link: a battle with one offers the free trial with the answers, a battle without
// one offers the answers only (no trial promised where none can be sent).
// 9 Oct: one behaviour for every battle. Each battle is created with its finish (DEFAULT_FINISH unless the Battle builder
// set one), and a row without it reads as DEFAULT_FINISH, so nothing reaches a phone without one.
import type { BattleConfig, FinishView } from './types';

/** The form's wording, without the switch. */
export type FinishWording = Omit<FinishView, 'askDetails'>;

/**
 * 9 Oct: the default wording for a battle with no trial link: the answers by email and no trial promised, as nothing
 * would send one. The thank-you says the email comes after the battle (the mailshot is sent by hand, so never "on its way").
 */
export const ANSWERS_WORDING: Readonly<FinishWording> = {
  heading: 'Get every answer explained',
  message: "Leave your details and we'll email you every question, the right answer and why.",
  button: 'Email me my answers',
  thanks: "Saved. We'll email your answers after the battle.",
};
/** The default wording once the battle has a trial link for the mailshot: the free trial, with the answers. */
export const TRIAL_WORDING: Readonly<FinishWording> = {
  heading: 'Get 14 days of MedSnapp free',
  message: "Add your details and we'll email your free trial, with every answer from this battle explained.",
  button: 'Claim my free trial',
  thanks: "Saved. We'll email your free trial and your answers after the battle.",
};
/** The wording a battle falls back to: the trial offer only when it has a trial link, otherwise the answers. */
export const defaultWording = (trialLink?: string): Readonly<FinishWording> => (typeof trialLink === 'string' && trialLink.trim() ? TRIAL_WORDING : ANSWERS_WORDING);

/**
 * The end of the battle when nobody has said otherwise: ask for details, with the wording of a battle without a trial
 * link (a battle made without one has none). A builder's empty wording is filled at read time by finishFor, from the
 * wording that matches the battle's trial link.
 */
export const DEFAULT_FINISH: FinishView = { askDetails: true, ...ANSWERS_WORDING };

/**
 * The form as a battle shows it, wording filled in: the battle's own finish, or DEFAULT_FINISH. Wording the builder set
 * wins; anything left empty takes the trial wording when the battle has a trial link, else the answers wording. A prize
 * always asks (its draw needs the form).
 */
export function finishFor(config?: Pick<BattleConfig, 'finish' | 'prize'>): FinishView {
  const f = config?.finish ?? DEFAULT_FINISH;
  const d = defaultWording(config?.finish?.trialLink);
  const pick = (v: string | undefined, fallback: string) => (typeof v === 'string' && v.trim() ? v.trim() : fallback);
  return {
    askDetails: f.askDetails === true || config?.prize?.enabled === true,
    heading: pick(f.heading, d.heading), message: pick(f.message, d.message), button: pick(f.button, d.button), thanks: pick(f.thanks, d.thanks),
  };
}

/** Whether a battle collects details at the end: its form is on (a battle without a finish is, by DEFAULT_FINISH). */
export function collectsDetails(config?: Pick<BattleConfig, 'finish' | 'prize'>): boolean {
  return finishFor(config).askDetails;
}

/** 1 September (UTC) of the academic year `now` falls in: a year of study saved before it may have moved on. */
export function academicYearStart(now: number): number {
  const d = new Date(now); const y = d.getUTCFullYear();
  return Date.UTC(d.getUTCMonth() >= 8 ? y : y - 1, 8, 1);
}

/** A player's trial link: {email} {first} {last} and {code} replaced, each URL-encoded. Empty without a template. */
export function fillTrialLink(template: string | undefined, v: { email?: string; first?: string; last?: string; code?: string }): string {
  if (!template) return '';
  const enc = (x?: string) => encodeURIComponent(x ?? '');
  return template.replace(/\{email\}/g, enc(v.email)).replace(/\{first\}/g, enc(v.first)).replace(/\{last\}/g, enc(v.last)).replace(/\{code\}/g, enc(v.code));
}
